import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAlerts } from '../contexts/AlertsContext';
import { useToast } from '../contexts/ToastContext';
import helperService from '../services/helperService';
import alertsService from '../services/alertsService';
import './CreateAlert.css';

const ALERT_TYPES = [
  { id: 'Range Fire', icon: 'local_fire_department', color: '#e63946' },
  { id: 'Flash Flood', icon: 'water', color: '#2980b9' },
  { id: 'Severe Weather', icon: 'cloud', color: '#7b8c9d' },
  { id: 'Livestock Emergency', icon: 'pets', color: '#e67e22' },
  { id: 'Equipment Failure', icon: 'construction', color: '#27ae60' },
  { id: 'Medical Emergency', icon: 'medical_services', color: '#c0392b' },
  { id: 'Other', icon: 'help_outline', color: '#8e44ad' },
];

const EMPTY_STATE = (
  <div className="create-alert-page">
    <div className="container"><p>Loading alert…</p></div>
  </div>
);

function EditAlert() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToast } = useToast();
  const { myAlerts } = useAlerts();

  const [alert, setAlert] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [type, setType] = useState('');
  const [severity, setSeverity] = useState('');
  const [description, setDescription] = useState('');
  const [neededEquipment, setNeededEquipment] = useState([]);
  const [neededSkills, setNeededSkills] = useState([]);
  const [equipmentCatalog, setEquipmentCatalog] = useState([]);
  const [skillsCatalog, setSkillsCatalog] = useState([]);
  const [catalogLoading, setCatalogLoading] = useState(true);

  const isOwner = myAlerts.some(a => String(a.id) === String(id));

  useEffect(() => {
    Promise.all([
      helperService.getEquipmentCatalog(),
      helperService.getSkillsCatalog(),
      alertsService.getAlert(id),
    ]).then(([eqCat, skCat, fetchedAlert]) => {
      setEquipmentCatalog(eqCat);
      setSkillsCatalog(skCat);
      setAlert(fetchedAlert);
      setType(fetchedAlert.type || '');
      setSeverity(fetchedAlert.severity || '');
      setDescription(fetchedAlert.description || '');
      setNeededEquipment((fetchedAlert.neededEquipment || []).map(i => i.id));
      setNeededSkills((fetchedAlert.neededSkills || []).map(i => i.id));
    }).catch(err => addToast(err.message || 'Could not load alert.', 'error')).finally(() => {
      setLoading(false);
      setCatalogLoading(false);
    });
  }, [id, addToast]);

  const toggle = (setter, itemId) =>
    setter(prev => prev.includes(itemId) ? prev.filter(i => i !== itemId) : [...prev, itemId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!type || !severity) {
      addToast('Type and severity are required.', 'error');
      return;
    }
    setSaving(true);
    try {
      await alertsService.updateAlert(Number(id), {
        type, severity, description, neededEquipment, neededSkills,
      });
      addToast('Alert updated.', 'success');
      navigate(`/alerts/${id}`);
    } catch (err) {
      addToast(err.message || 'Could not update alert.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const backButton = (label, to) => (
    <button className="button" onClick={() => navigate(to)}>{label}</button>
  );

  if (loading) return EMPTY_STATE;
  if (!isOwner) return (
    <div className="create-alert-page">
      <div className="container">
        <p>You can only edit your own alerts.</p>
        {backButton('Back to Alert', `/alerts/${id}`)}
      </div>
    </div>
  );
  if (!alert || alert.status !== 'active') return (
    <div className="create-alert-page">
      <div className="container">
        <p>Only open alerts can be edited.</p>
        {backButton('Back to Alert', `/alerts/${id}`)}
      </div>
    </div>
  );

  return (
    <div className="create-alert-page">
      <div className="container">
        <h1>Edit Alert</h1>
        <form onSubmit={handleSubmit} className="alert-form">
          <h2 className="phase-title">Alert Details</h2>

          <div className="form-group">
            <label>Location</label>
            <div className="preselected-info">
              <span className="material-icons">location_on</span>
              <span>{alert.location}</span>
            </div>
          </div>

          <div className="form-group">
            <label>Type</label>
            <div className="type-tiles">
              {ALERT_TYPES.map(t => (
                <button
                  key={t.id} type="button" aria-label={t.id}
                  className={`type-tile${type === t.id ? ' selected' : ''}`}
                  onClick={() => setType(t.id)}
                >
                  <span className="material-icons type-icon" style={{ color: t.color }}>{t.icon}</span>
                  <span className="type-label">{t.id}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="form-group">
            <label>Severity</label>
            <div className="severity-buttons">
              <button type="button" className={`severity-btn low${severity === 'Low' ? ' selected' : ''}`} onClick={() => setSeverity('Low')}>Low</button>
              <button type="button" className={`severity-btn medium${severity === 'Medium' ? ' selected' : ''}`} onClick={() => setSeverity('Medium')}>Medium</button>
              <button type="button" className={`severity-btn high${severity === 'High' ? ' selected' : ''}`} onClick={() => setSeverity('High')}>High<span className="severity-hint">Call 911 first</span></button>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="description">Description</label>
            <textarea id="description" rows="4" value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="What's happening? Any details responders should know..."
            />
          </div>

          <div className="cap-section">
            <h3>Equipment Needed</h3>
            <p className="section-hint">Select the equipment that would help responders.</p>
            <div className="chip-grid">
              {catalogLoading ? <p className="section-hint">Loading…</p> : equipmentCatalog.map(item => (
                <button key={item.id} type="button"
                  className={`chip${neededEquipment.includes(item.id) ? ' selected' : ''}`}
                  onClick={() => toggle(setNeededEquipment, item.id)}>{item.label}</button>
              ))}
            </div>
          </div>

          <div className="cap-section">
            <h3>Skills &amp; Certifications Needed</h3>
            <p className="section-hint">Select relevant skills or certifications for responders.</p>
            <div className="chip-grid">
              {catalogLoading ? <p className="section-hint">Loading…</p> : skillsCatalog.map(item => (
                <button key={item.id} type="button"
                  className={`chip${neededSkills.includes(item.id) ? ' selected' : ''}`}
                  onClick={() => toggle(setNeededSkills, item.id)}>{item.label}</button>
              ))}
            </div>
          </div>

          <div className="form-buttons">
            <button type="submit" className="submit-button" disabled={saving}>
              {saving ? 'Saving…' : 'Save Changes'}
            </button>
            <button type="button" className="cancel-button" onClick={() => navigate(`/alerts/${id}`)}>Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default EditAlert;
